/**
 * CurrentTeamFieldContainer — the Current Team field's bindings.
 *
 * Two things the View may not do: keep the input's change handler stable while
 * it ALSO drives the suggestion search, and bind each suggestion's press to its
 * own team. Both were in `ManuallyAddPlayerModalView`, which is why that file
 * had a hook at all (SE-8019).
 * @module components/organisms/ManuallyAddPlayerModal/CurrentTeamField/CurrentTeamFieldContainer
 */

import React, { memo, useCallback, useMemo } from "react";
import type { ControllerRenderProps } from "react-hook-form";

import CurrentTeamFieldView from "./CurrentTeamFieldView";

import { IOptionSelect } from "@/types/customSelectTypes";
import { IManuallyAddPlayerForm } from "@/types/manuallyAddPlayerModalTypes";

/** How many characters the search needs before suggestions appear. */
const MIN_SEARCH_LENGTH = 2;

/** Props for {@link CurrentTeamFieldContainer}. */
export interface CurrentTeamFieldContainerProps {
  /** Whether dark mode is active. */
  readonly isDark: boolean;
  /** Current team search text, which gates the suggestion list. */
  readonly teamSearchText: string;
  /** Team autocomplete suggestion options. */
  readonly teamSuggestions: readonly IOptionSelect[];
  /** Team search text change handler. */
  readonly onTeamSearchChange: (text: string) => void;
  /** Team suggestion selection handler. */
  readonly onTeamSuggestionSelect: (team: IOptionSelect) => void;
  /** The react-hook-form field. */
  readonly field: ControllerRenderProps<IManuallyAddPlayerForm, "currentTeam">;
}

/**
 * Binds the field's handlers and renders it.
 * @param root0 - Field props
 * @param root0.isDark - Whether dark mode is active
 * @param root0.teamSearchText - Current team search text
 * @param root0.teamSuggestions - Suggestion options
 * @param root0.onTeamSearchChange - Search text change handler
 * @param root0.onTeamSuggestionSelect - Suggestion selection handler
 * @param root0.field - The react-hook-form field
 * @returns The bound field.
 */
const CurrentTeamFieldContainer = ({
  isDark,
  teamSearchText,
  teamSuggestions,
  onTeamSearchChange,
  onTeamSuggestionSelect,
  field,
}: CurrentTeamFieldContainerProps) => {
  const { onChange, onBlur, value } = field;

  const handleChangeText = useCallback(
    (text: string) => {
      onChange(text);
      onTeamSearchChange(text);
    },
    [onChange, onTeamSearchChange]
  );

  const suggestionHandlers = useMemo(
    () =>
      Object.fromEntries(
        teamSuggestions.map(team => [
          team.value,
          () => {
            onChange(team.label);
            onTeamSuggestionSelect(team);
          },
        ])
      ),
    [teamSuggestions, onChange, onTeamSuggestionSelect]
  );

  return (
    <CurrentTeamFieldView
      isDark={isDark}
      value={value ?? ""}
      showSuggestions={
        teamSearchText.length >= MIN_SEARCH_LENGTH && teamSuggestions.length > 0
      }
      teamSuggestions={teamSuggestions}
      suggestionHandlers={suggestionHandlers}
      onChangeText={handleChangeText}
      onBlur={onBlur}
    />
  );
};

CurrentTeamFieldContainer.displayName = "CurrentTeamFieldContainer";

export default memo(CurrentTeamFieldContainer);

/**
 * Builds the `Controller` render prop for the Current Team field.
 *
 * `Controller` takes a render function, so the modal binds the screen-level
 * values here and the field itself stays a component.
 * @param isDark - Whether dark mode is active.
 * @param teamSearchText - Current team search text.
 * @param teamSuggestions - Suggestion options.
 * @param onTeamSearchChange - Search text change handler.
 * @param onTeamSuggestionSelect - Suggestion selection handler.
 * @returns The render prop.
 */
export const renderCurrentTeamField =
  (
    isDark: boolean,
    teamSearchText: string,
    teamSuggestions: readonly IOptionSelect[],
    onTeamSearchChange: (text: string) => void,
    onTeamSuggestionSelect: (team: IOptionSelect) => void
  ) =>
  ({
    field,
  }: {
    field: ControllerRenderProps<IManuallyAddPlayerForm, "currentTeam">;
  }) => (
    <CurrentTeamFieldContainer
      isDark={isDark}
      teamSearchText={teamSearchText}
      teamSuggestions={teamSuggestions}
      onTeamSearchChange={onTeamSearchChange}
      onTeamSuggestionSelect={onTeamSuggestionSelect}
      field={field}
    />
  );
